import React, { useEffect, useState } from "react";
import { Button, Col, Form, InputGroup, Row, Spinner, Toast, ToastContainer } from "react-bootstrap";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import packageJson from '../../../../../../package.json';
import { AccountStrings, IFormSignupComponent } from "../../../business/core";
import { FormRecoveryControllerImpl } from "../../../business/impl";

export const FormRecoveryPasswordComponent: React.FC<IFormSignupComponent> = ({ setStep }) => {
    const navigate = useNavigate();
    const [validated, setValidated] = useState(false);
    const [password, setPassword] = useState<string>('');
    const [passwordConfirm, setPasswordConfirm] = useState<string>('');
    const [passwordError, setPasswordError] = useState(false);
    const [passwordConfirmError, setPasswordConfirmError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [toastingText, setToastingText] = useState<string | null>(null);
    const [strengthPasswordLevel, setStrengthPasswordLevel] = useState<string>('Moderate');
    const [showPassword, setShowPassword] = useState(false);

    const version = packageJson.version;

    const controller = new FormRecoveryControllerImpl();

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const response = await controller.onLoad();
            setStrengthPasswordLevel(response);
        } catch (error) {
            console.error("Erro na requisição:", error);
        }
        setIsLoading(false);
    }

    useEffect(() => {
        fetchData();
    }, [strengthPasswordLevel]);

    const translate = AccountStrings['PTBR'];

    const handleSubmit = async (event) => {
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();

        setValidated(true);
        setPasswordError(false);
        setPasswordConfirmError(false);

        if (!password || password === "") {
            setValidated(false);
            setPasswordError(true);
            setIsLoading(false);
            return false;
        }
        if (passwordConfirm !== password) {
            setValidated(false);
            setPasswordConfirmError(true);
            setIsLoading(false);
            return false;
        }
        const submitedFormData = new FormData();
        submitedFormData.append("password", btoa(password));

        if (form.checkValidity() === false) {
        } else {
            setIsLoading(true);
            try {
                const response = await controller.recoveryNewPassword(submitedFormData);
                if (response.success) {
                    localStorage.setItem('global-toast', JSON.stringify({ type: 'success', message: translate.signup.signupSuccess }));
                    navigate('/auth', { state: { reload: true } });
                    return true;
                }

                switch (response.response) {
                    case "password":
                        setPasswordError(true);
                        break;
                    default:
                        setToastingText(response.response)
                        break
                }
            } catch (error) {
                setToastingText(translate.recovery.errors[error.message]);
            }
            window.scrollTo(0, 0);
            setIsLoading(false);
        }
    };

    return (
        <>
            <Form className="mt-4" onSubmit={handleSubmit} validated={validated}>
                <Form.Group className="mb-3" controlId="formBasicPassword">
                    <InputGroup hasValidation>
                        <Form.Control
                            type={showPassword ? 'text' : 'password'}
                            required
                            minLength={6}
                            placeholder={translate.signup.placeholder.password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="custom-input"
                            isInvalid={passwordError}
                            disabled={isLoading}
                            value={password}
                        />
                        <Button variant="outline-secondary"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </Button>
                        <Form.Control.Feedback type="invalid">
                            {translate.signup.feedback.password}
                        </Form.Control.Feedback>
                    </InputGroup>
                    <Form.Text id="passwordHelpBlock" muted>
                        <p dangerouslySetInnerHTML={{ __html: translate.signup.password_guide[strengthPasswordLevel] }} />
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3" controlId="formBasicPasswordConfirm">
                    <Form.Control
                        type={showPassword ? 'text' : 'password'}
                        required
                        minLength={6}
                        placeholder={translate.signup.placeholder.passwordConfirm}
                        onChange={(e) => setPasswordConfirm(e.target.value)}
                        className="custom-input"
                        isInvalid={passwordConfirmError}
                        disabled={isLoading}
                        value={passwordConfirm}
                    />
                </Form.Group>
                <fieldset disabled={isLoading}>
                    <div className="row">
                        <div className="col">
                            <Button
                                variant="primary"
                                type="button"
                                className="px-4 button-flat"
                                onClick={() => setStep(2)}
                                disabled={isLoading}
                            >
                                {!isLoading ? (
                                    translate.recovery.backButton
                                ) : (
                                    <Spinner animation="border" size="sm" role="status">
                                        <span className="visually-hidden">...</span>
                                    </Spinner>
                                )}
                            </Button>
                        </div>
                        <div className="col d-flex flex-row-reverse">
                            <Button
                                variant="primary"
                                type="submit"
                                className="px-4 button-flat"
                                disabled={isLoading}
                            >
                                {!isLoading ? (
                                    translate.recovery.requestRecoveryButton
                                ) : (
                                    <Spinner animation="border" size="sm" role="status">
                                        <span className="visually-hidden">...</span>
                                    </Spinner>
                                )}
                            </Button>
                        </div>
                    </div>
                    <Row>
                        <Col className="text-center mt-2"><small style={{ color: '#999999' }}>v{version}</small></Col>
                    </Row>
                </fieldset>
            </Form>
            <ToastContainer position="top-end">
                <Toast
                    bg={"danger"}
                    onClose={() => setToastingText(null)}
                    show={toastingText !== null ? true : false}
                    delay={5000}
                    autohide
                >
                    <Toast.Body className={"text-white"}>
                        {toastingText !== null ? toastingText : null}
                    </Toast.Body>
                </Toast>
            </ToastContainer>
        </>
    );
};
